/**
 * Single source of truth for "what is the workbench preview doing right now, and what
 * should we tell the user" — merges what used to be two independently-computed pieces of
 * `Workbench.tsx` (a priority-ordered ladder, plus a separate hardcoded override for the
 * iframe/bridge wait) into one ordered derivation. Pure — no React, no store reads — so
 * `Workbench.tsx` stays the only place that reads store atoms and passes their values in.
 */

export type WorkbenchLoadingPhase =
  | 'installing-deps'
  | 'connecting-fast-preview'
  | 'building'
  | 'applying-changes'
  | 'generating'
  | 'build-error'
  | 'reconnecting-snapshot'
  | 'waiting-for-app-mount'
  | 'idle-empty'
  | 'ready';

export interface WorkbenchLoadingPhaseInput {
  depsInstalled: boolean;
  buildStatus: 'idle' | 'building' | 'success' | 'error';
  isStreaming: boolean;
  clientToolsBusy: boolean;
  previewUrl: string;
  snapshotPreviewReady: boolean;
  preferFastDevPreview: boolean;
  /** Whether the preview iframe is currently allowed to be shown at all. */
  previewInteractive: boolean;
  /** Which condition made `previewInteractive` true — lets the app-mount wait continue
   * whatever phrase was already showing instead of jumping to an unrelated one. */
  previewInteractiveReason: 'success' | 'snapshot' | 'fast-dev' | null;
  previewIframeLoaded: boolean;
  bridgeReady: boolean;
  bridgeReadyTimedOut: boolean;
}

export interface WorkbenchLoadingPhaseResult {
  phase: WorkbenchLoadingPhase;
  message: string;
  variant: 'generating' | 'plain';
  /** True once there's actually something interactive on screen. */
  isReady: boolean;
}

export function deriveWorkbenchLoadingPhase(
  input: WorkbenchLoadingPhaseInput,
): WorkbenchLoadingPhaseResult {
  const {
    depsInstalled,
    buildStatus,
    isStreaming,
    clientToolsBusy,
    previewUrl,
    snapshotPreviewReady,
    preferFastDevPreview,
    previewInteractive,
    previewInteractiveReason,
    previewIframeLoaded,
    bridgeReady,
    bridgeReadyTimedOut,
  } = input;

  if (!depsInstalled) {
    return { phase: 'installing-deps', message: 'Setting up your prototype workspace…', variant: 'plain', isReady: false };
  }

  if (preferFastDevPreview && previewUrl && buildStatus !== 'success' && buildStatus !== 'error') {
    return { phase: 'connecting-fast-preview', message: 'Connecting to your live preview…', variant: 'plain', isReady: false };
  }

  if (buildStatus === 'building') {
    return { phase: 'building', message: 'Building and checking your prototype…', variant: 'plain', isReady: false };
  }

  if (clientToolsBusy) {
    return { phase: 'applying-changes', message: 'Applying the latest changes…', variant: 'plain', isReady: false };
  }

  if (isStreaming && buildStatus !== 'success') {
    return { phase: 'generating', message: 'Generating your prototype…', variant: 'generating', isReady: false };
  }

  if (buildStatus === 'error') {
    return {
      phase: 'build-error',
      message: 'The last build hit an error — check the Terminal, then Rebuild to try again.',
      variant: 'plain',
      isReady: false,
    };
  }

  if (snapshotPreviewReady && previewUrl) {
    // `snapshotPreviewReady` stays true for the rest of the session once a restored/old
    // prototype is shown (it's only cleared by a full template reset or a fresh rebuild) —
    // so this can't unconditionally report "not ready" the way it used to, or viewing any
    // restored prototype leaves `isReady` (and therefore Publish) stuck disabled forever even
    // though the iframe is already showing the working app. Fall back to the same
    // bridge-confirmed-mount signal (with the same 15s timeout escape hatch) the live-build
    // path uses below, instead of a flag that never resets.
    const mounted = bridgeReady || bridgeReadyTimedOut;
    return {
      phase: 'reconnecting-snapshot',
      message: 'Reconnecting to your saved prototype…',
      variant: 'plain',
      isReady: mounted,
    };
  }

  // App has been given the go-ahead to show (build succeeded / snapshot restored / fast dev
  // preview), but the iframe hasn't finished loading or the app hasn't mounted inside it yet.
  // Continue whatever phrase already applied to `previewInteractive` becoming true instead of
  // jumping to an unrelated one — the one genuinely new wait is the post-build-success case,
  // where nothing earlier in this ladder has said anything about the preview yet.
  //
  // `bridgeReadyTimedOut` is the 15s escape hatch: once it fires we stop waiting on EITHER
  // signal, not just the bridge — a dev server that never fires the iframe's native `load`
  // event at all (a stalled network layer, not a broken generated app) must still eventually
  // give up and show the iframe, or this phase never exits.
  if (previewInteractive && !bridgeReadyTimedOut && (!previewIframeLoaded || !bridgeReady)) {
    const message =
      previewInteractiveReason === 'fast-dev'
        ? 'Connecting to your live preview…'
        : previewInteractiveReason === 'snapshot'
          ? 'Reconnecting to your saved prototype…'
          : 'Loading your prototype preview…';
    return { phase: 'waiting-for-app-mount', message, variant: 'plain', isReady: false };
  }

  if (preferFastDevPreview && previewUrl) {
    return { phase: 'connecting-fast-preview', message: 'Connecting to your live preview…', variant: 'plain', isReady: false };
  }

  if (!previewInteractive) {
    return {
      phase: 'idle-empty',
      message: "Your prototype preview will appear here once it's ready.",
      variant: 'plain',
      isReady: false,
    };
  }

  return { phase: 'ready', message: '', variant: 'plain', isReady: true };
}
